import styled, { css } from 'styled-components';

import { Text } from '@/nova/components';
import { ANIM, COLORS, MOBILE, SPACE } from '@/nova/theme/tokens';

/* "Where should this link send people?": a single-choice stack of cards (the casino, an existing landing page, a
   quick landing page, a custom one), then the footer that follows the choice. Colours, timings and spacing come only
   from theme tokens. */

export const Intro = styled(Text).attrs({ tag: 'paragraph' as const })`
  margin: 0 0 ${SPACE[12]};
  font-size: 13px;
  color: ${COLORS.textMuted};
`;

/** The choices, one above the other at every width (each is a full row; on a phone the sheet scrolls them). */
export const Choices = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[8]};
`;

/** One choice: a bordered card, lit when chosen, faded when it cannot be chosen. */
export const Choice = styled.div<{ $selected: boolean; $disabled?: boolean }>`
  display: flex;
  flex-direction: column;
  border: 1px solid ${({ $selected }) => ($selected ? COLORS.accent : COLORS.hairline)};
  border-radius: 12px;
  background: ${COLORS.surfaceInset};
  opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};
  transition:
    border-color ${ANIM.INTERACT_MS}ms ${ANIM.EASE},
    opacity ${ANIM.INTERACT_MS}ms ${ANIM.EASE};
`;

/** The clickable head of a choice: its icon, title and description, and the radio dot. */
export const ChoiceHead = styled.button`
  display: flex;
  align-items: flex-start;
  gap: ${SPACE[12]};
  width: 100%;
  padding: ${SPACE[12]};
  border: 0;
  border-radius: 12px;
  background: transparent;
  color: inherit;
  text-align: left;
  cursor: pointer;
  transition: background ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &:hover:not(:disabled) {
    background: ${COLORS.hoverWash};
  }

  &:disabled {
    cursor: default;
  }

  &:focus-visible {
    outline: 2px solid ${COLORS.accent};
    outline-offset: -2px;
  }
`;

/** The choice's icon in a small tile. */
export const ChoiceIcon = styled.span`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: 1px solid ${COLORS.hairline};
  color: ${COLORS.textMuted};
`;

export const ChoiceCopy = styled.span`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[2]};
  flex: 1 1 auto;
  min-width: 0;
`;

export const ChoiceTitle = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 14px;
  font-weight: 600;
  color: ${COLORS.text};
`;

export const ChoiceText = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 12px;
  color: ${COLORS.textMuted};
`;

/** An address (`brand.com/promotions/welcome`): data, ellipsised when long. */
export const Where = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 12px;
  color: ${COLORS.text};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/** Why a choice cannot be taken (no website address, no marketing offer, no price). */
export const ChoiceReason = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 12px;
  color: ${COLORS.warning};
`;

/** The quick landing page's price ("12 credits"). */
export const Price = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 12px;
  font-weight: 600;
  color: ${COLORS.text};
`;

/** The price's placeholder while the dry run answers: one short line. */
export const PriceSkeleton = styled.span`
  display: block;
  width: 72px;
  height: 14px;
  border-radius: 6px;
  background: ${COLORS.surfaceMuted};
`;

/** The radio dot at the right of a choice (and of a landing page row). */
export const Radio = styled.span<{ $on: boolean }>`
  flex: 0 0 auto;
  width: 18px;
  height: 18px;
  /* Optical: centred on the title's first line (20px line box, 18px dot). */
  margin-top: 1px;
  border-radius: 999px;
  border: 2px solid ${({ $on }) => ($on ? COLORS.accent : COLORS.radioBorder)};
  background: transparent;
  transition: border-color ${ANIM.INTERACT_MS}ms ${ANIM.EASE};
  ${({ $on }) =>
    $on &&
    css`
      box-shadow: inset 0 0 0 3px ${COLORS.surfaceInset};
      background: ${COLORS.accent};
    `}
`;

/** The brand's landing pages in the open "An existing landing page" section: every page at its full height, so the
 *  sheet's own scroll moves through them (a list scrolling inside the sheet stole the sheet's scroll). */
export const PageList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[2]};
`;

/** The accordion: one section per destination, the open one is the pick. */
export const Sections = styled.div`
  display: flex;
  flex-direction: column;
`;

/** An open section's explanation and details (address, price, reason). */
export const SectionBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[4]};
`;

/** One landing page: its picture, name and address, and the radio dot. */
export const PageRow = styled.button<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  gap: ${SPACE[12]};
  width: 100%;
  padding: ${SPACE[8]};
  border: 1px solid ${({ $selected }) => ($selected ? COLORS.hoverBorderLit : 'transparent')};
  border-radius: 10px;
  background: ${({ $selected }) => ($selected ? COLORS.hoverWash : 'transparent')};
  color: inherit;
  text-align: left;
  cursor: pointer;
  transition:
    background ${ANIM.INTERACT_MS}ms ${ANIM.EASE},
    border-color ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &:hover {
    background: ${COLORS.hoverWash};
  }

  &:focus-visible {
    outline: 2px solid ${COLORS.accent};
    outline-offset: -2px;
  }

  & > ${Radio} {
    margin-top: 0;
  }
`;

/** One landing page in the list: the selectable row, with its "open in a new tab" link laid over the row's link slot
 *  (a link cannot sit inside the row's button, so it is the row's sibling, positioned over the slot). */
export const PageItem = styled.div`
  position: relative;
`;

/** The space the open link takes inside the row, just before the radio dot. */
export const LinkSlot = styled.span`
  flex: 0 0 auto;
  width: ${SPACE[32]};
  height: ${SPACE[32]};
`;

/** "Open in a new tab": over the row's link slot. Its right edge = the row's padding, border, radio and gap
 *  (8 + 1 + 18 + 12 = 39px, off the scale: it lines the link up with the slot). */
export const OpenLink = styled.a`
  position: absolute;
  top: 50%;
  right: 39px;
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${SPACE[32]};
  height: ${SPACE[32]};
  border-radius: 999px;
  color: ${COLORS.textMuted};
  transition:
    background ${ANIM.INTERACT_MS}ms ${ANIM.EASE},
    color ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &:hover {
    color: ${COLORS.text};
    background: ${COLORS.hoverWash};
  }

  &:focus-visible {
    outline: 2px solid ${COLORS.accent};
    outline-offset: 0;
  }
`;

/** The page's picture: its key art, else a neutral tile. A phone-ish portrait crop (the art is a page's first
 *  screen). */
export const Thumb = styled.span`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 52px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid ${COLORS.hairline};
  background: ${COLORS.surfaceMuted};
  color: ${COLORS.textFaint};
`;

export const ThumbImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

export const PageCopy = styled.span`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[2]};
  flex: 1 1 auto;
  min-width: 0;
`;

export const PageName = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 13px;
  font-weight: 600;
  color: ${COLORS.text};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/** A row's placeholder while the brand's pages are read. */
export const PageRowSkeleton = styled.span`
  display: block;
  height: 52px;
  margin: ${SPACE[8]};
  border-radius: 8px;
  background: ${COLORS.surfaceMuted};
`;

/** The list's empty line. */
export const Empty = styled(Text).attrs({ tag: 'paragraph' as const })`
  margin: 0;
  padding: ${SPACE[8]};
  font-size: 12px;
  color: ${COLORS.textFaint};
`;

/** The footer: an optional notice above the buttons. */
export const FooterStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[12]};
  width: 100%;
`;

export const FooterRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: ${SPACE[8]};
`;
